import { useRef, useState, useCallback, useEffect } from 'react'
import { X, Loader2 } from 'lucide-react'
import { useGoogleMaps } from '@/hooks/useGoogleMaps'
import { cn } from '@/lib/utils'

interface AddressAutocompleteProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  required?: boolean
  className?: string
}

interface Prediction {
  placeId: string
  mainText: string
  secondaryText: string
  fullText: string
}

export function AddressAutocomplete({
  value,
  onChange,
  placeholder = '輸入地址...',
  disabled = false,
  required = false,
  className,
}: AddressAutocompleteProps) {
  const { isLoaded, error } = useGoogleMaps()
  const [isOpen, setIsOpen] = useState(false)
  const [predictions, setPredictions] = useState<Prediction[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const serviceRef = useRef<google.maps.places.AutocompleteService | null>(null)
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    if (isLoaded && window.google?.maps?.places) {
      serviceRef.current = new window.google.maps.places.AutocompleteService()
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken()
    }
  }, [isLoaded])

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const fetchPredictions = useCallback(
    (input: string) => {
      if (!input.trim() || !serviceRef.current || !sessionTokenRef.current) {
        setPredictions([])
        return
      }
      setIsSearching(true)
      serviceRef.current.getPlacePredictions(
        {
          input,
          componentRestrictions: { country: 'tw' },
          sessionToken: sessionTokenRef.current,
        },
        (predictions, status) => {
          setIsSearching(false)
          if (status !== 'OK' || !predictions) {
            setPredictions([])
            return
          }
          setPredictions(
            predictions.map((p) => ({
              placeId: p.place_id,
              mainText: p.structured_formatting.main_text || p.description,
              secondaryText: p.structured_formatting.secondary_text || '',
              fullText: p.description,
            }))
          )
          setSelectedIndex(-1)
        }
      )
    },
    []
  )

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value)
    setIsOpen(true)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchPredictions(e.target.value), 300)
  }

  const handleSelect = (prediction: Prediction) => {
    onChange(prediction.fullText)
    setIsOpen(false)
    setPredictions([])
    if (window.google?.maps?.places) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || predictions.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex((i) => Math.min(i + 1, predictions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex((i) => Math.max(i - 1, -1))
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault()
      handleSelect(predictions[selectedIndex])
    } else if (e.key === 'Escape') {
      setIsOpen(false)
    }
  }

  // Fallback to plain input if API key not configured
  if (error) {
    return (
      <div className="relative w-full">
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          autoComplete="off"
          className={cn(
            'flex h-10 w-full rounded-md border border-input bg-white dark:bg-gray-800 px-3 py-2 pr-8 text-sm shadow-sm outline-none focus:ring-1 focus:ring-primary placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
            className
          )}
        />
        {value && (
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={14} />
          </button>
        )}
      </div>
    )
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <input
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => value && setIsOpen(true)}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        autoComplete="off"
        className={cn(
          'flex h-10 w-full rounded-md border border-input bg-white dark:bg-gray-800 px-3 py-2 pr-8 text-sm shadow-sm outline-none focus:ring-1 focus:ring-primary placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
      />
      {value && (
        <button
          type="button"
          onClick={() => { onChange(''); setPredictions([]); setIsOpen(false) }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X size={14} />
        </button>
      )}

      {isOpen && (predictions.length > 0 || isSearching) && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-[100]">
          {isSearching && predictions.length === 0 ? (
            <div className="flex items-center justify-center py-3 text-gray-500 text-sm gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              搜尋中...
            </div>
          ) : (
            <ul className="max-h-60 overflow-y-auto py-1">
              {predictions.map((p, i) => (
                <li key={p.placeId}>
                  <button
                    type="button"
                    onClick={() => handleSelect(p)}
                    onMouseEnter={() => setSelectedIndex(i)}
                    className={cn(
                      'w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors',
                      i === selectedIndex && 'bg-gray-50 dark:bg-gray-700'
                    )}
                  >
                    <div className="text-sm font-medium text-gray-800 dark:text-gray-200">{p.mainText}</div>
                    {p.secondaryText && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">{p.secondaryText}</div>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

export default AddressAutocomplete
